import { Button, Col, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { say } from "../../localization/say.ts";
import { COMPOSER_ROW } from "../composer/composer-state.ts";

export type AppChoice = { placement: string; name: string; context?: string };

export function dismissesAppMenu(target: unknown): boolean {
  const closest = (target as { closest?: (selector: string) => unknown } | null)?.closest;
  if (typeof closest !== "function") return true;
  return !closest.call(target, '[data-testid="composer-apps"]');
}

export type LabelledApp = { placement: string; name: string; note: string | null };

export function labelledApps(apps: readonly AppChoice[]): LabelledApp[] {
  const seen = new Map<string, number>();
  for (const app of apps) seen.set(app.name, (seen.get(app.name) ?? 0) + 1);
  return apps.map((app) => ({
    placement: app.placement,
    name: app.name,
    note: (seen.get(app.name) ?? 0) > 1 ? whereItSits(app.context) : null,
  }));
}

function whereItSits(context: string | undefined): string | null {
  if (context === "conversation") return say("apps.option.conversation");
  if (context === "project") return say("apps.option.project");
  if (context === "service") return say("apps.option.service");
  return null;
}

export function AppAttachments({
  apps,
  selected,
  toggle,
}: {
  apps: readonly AppChoice[];
  selected: readonly string[];
  toggle: (id: string) => void;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const pressed = (event: PointerEvent) => {
      if (dismissesAppMenu(event.target)) setOpen(false);
    };
    const keyed = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", pressed);
    document.addEventListener("keydown", keyed);
    return () => {
      document.removeEventListener("pointerdown", pressed);
      document.removeEventListener("keydown", keyed);
    };
  }, [open]);
  if (apps.length === 0) return null;
  return (
    <Col style={{ position: "relative" }} testID="composer-apps">
      {open ? (
        <Col
          testID="composer-apps-menu"
          style={{
            position: "absolute",
            bottom: 34,
            right: 0,
            minWidth: 180,
            padding: theme.space[2],
            gap: theme.space[1],
            backgroundColor: theme.color["bg.surface"],
            borderWidth: 1,
            borderColor: theme.color["border.default"],
            borderRadius: theme.radius.md,
            ...theme.elevation.float,
            zIndex: 10,
          }}
        >
          <Text typeName="caption" color="text.tertiary">
            {say("apps.attach")}
          </Text>
          {labelledApps(apps).map((app) => (
            <Button
              key={app.placement}
              size="sm"
              variant={selected.includes(app.placement) ? "primary" : "soft"}
              onPress={() => toggle(app.placement)}
            >
              {app.note ? `${app.name} · ${app.note}` : app.name}
            </Button>
          ))}
        </Col>
      ) : null}
      <Tappable
        onPress={() => setOpen((held) => !held)}
        accessibilityRole="button"
        accessibilityLabel={say("apps.attach")}
        accessibilityState={{ expanded: open }}
        testID="composer-apps-trigger"
        hitSlop={7}
        style={{
          height: COMPOSER_ROW,
          paddingHorizontal: theme.space[3],
          borderRadius: theme.radius.md,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.color["bg.surfaceAlt"],
        }}
      >
        <Text typeName="label" color="text.secondary">
          {selected.length > 0 ? `Apps (${selected.length})` : "Apps"}
        </Text>
      </Tappable>
    </Col>
  );
}
